import React, { useEffect, useMemo, useState } from 'react';
import { C, F, T, Btn, Pill, Card, Shell, PageTitle } from '../theme/uiKit';
import { getSupplierControlService } from '../services';
import type {
    ControlSupplier,
    ControlSite,
    ControlRequest,
    ControlUser,
    UserType,
} from '../services/types';

// Supplier Control — internal access management. Ported from the dscs
// prototypes (Suppliers directory + Approval Queue + Supplier Review), wired to
// the SupplierControlService. Data is empty until the backend is built.

type ControlView = 'suppliers' | 'queue' | 'review' | 'users';

const CONTROL_NAV = [
    { id: 'suppliers', label: 'Suppliers' },
    { id: 'queue', label: 'Approval Queue' },
    { id: 'users', label: 'Users' },
];

// ── Manage-access drawer (one login's site access) ────────────────────────────
function ManageDrawer({
    email,
    sites,
    access,
    onGrant,
    onRevoke,
    onClose,
}: {
    email: string;
    sites: ControlSite[];
    access: Record<string, boolean>;
    onGrant: (siteId: string) => void;
    onRevoke: (site: ControlSite) => void;
    onClose: () => void;
}) {
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = sites.filter(
        (s) =>
            query === '' ||
            s.name.toLowerCase().includes(query) ||
            s.code.toLowerCase().includes(query) ||
            s.location.toLowerCase().includes(query),
    );
    const granted = sites.filter((s) => access[s.id]).length;

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 30 }}>
            <button
                type="button"
                aria-label="Close manage access"
                onClick={onClose}
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.35)',
                    border: 'none',
                    padding: 0,
                    cursor: 'default',
                }}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Manage access"
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 480,
                    maxWidth: '100%',
                    background: C.surface,
                    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: F.ui,
                }}
            >
                <div
                    style={{
                        padding: '16px 20px',
                        borderBottom: `1px solid ${C.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div>
                        <div style={{ fontSize: T.section, fontWeight: 700, color: C.text }}>
                            Manage access
                        </div>
                        <div style={{ fontSize: T.caption, color: C.textMuted, fontFamily: F.mono }}>
                            {email}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: 22,
                            cursor: 'pointer',
                            color: C.textMuted,
                        }}
                    >
                        ×
                    </button>
                </div>
                <div
                    style={{
                        padding: '12px 20px',
                        borderBottom: `1px solid ${C.borderLight}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                    }}
                >
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search sites by name or code…"
                        style={{
                            flex: 1,
                            height: 36,
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            padding: '0 12px',
                            fontSize: T.body,
                            fontFamily: F.ui,
                            boxSizing: 'border-box',
                        }}
                    />
                    <span style={{ fontSize: T.caption, color: C.textMuted, whiteSpace: 'nowrap' }}>
                        {granted} of {sites.length}
                    </span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {shown.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: C.textMuted }}>
                            {sites.length === 0 ? 'No sites to show yet.' : 'No matching sites.'}
                        </div>
                    ) : (
                        shown.map((s) => {
                            const has = access[s.id];
                            return (
                                <div
                                    key={s.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '12px 20px',
                                        borderBottom: `1px solid ${C.borderLight}`,
                                    }}
                                >
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: T.body, fontWeight: 600, color: C.text }}>
                                                {s.name}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: T.caption,
                                                    fontFamily: F.mono,
                                                    color: C.textSub,
                                                    background: C.graySoft,
                                                    borderRadius: 4,
                                                    padding: '1px 6px',
                                                }}
                                            >
                                                {s.code}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: T.caption, color: C.textMuted, marginTop: 2 }}>
                                            {s.location}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Pill
                                            label={has ? 'Access: Yes' : 'No access'}
                                            color={has ? 'green' : 'gray'}
                                            dot={has}
                                        />
                                        {has ? (
                                            <Btn
                                                label="Revoke"
                                                variant="danger"
                                                size="xs"
                                                onClick={() => onRevoke(s)}
                                            />
                                        ) : (
                                            <Btn
                                                label="Grant"
                                                variant="pri"
                                                size="xs"
                                                onClick={() => onGrant(s.id)}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Suppliers directory ───────────────────────────────────────────────────────
function SuppliersView({ suppliers }: { suppliers: ControlSupplier[] }) {
    const [state, setState] = useState<ControlSupplier[]>(suppliers);
    useEffect(() => setState(suppliers), [suppliers]);

    const [selectedId, setSelectedId] = useState<string>(suppliers[0]?.id ?? '');
    const [q, setQ] = useState('');
    const [manageEmail, setManageEmail] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<{ email: string; site: ControlSite } | null>(null);

    const query = q.trim().toLowerCase();
    const list = state.filter((s) => query === '' || s.name.toLowerCase().includes(query));
    const supplier = state.find((s) => s.id === selectedId) ?? state[0];
    const managed = supplier?.logins.find((l) => l.email === manageEmail) ?? null;

    const setAccess = (email: string, siteId: string, granted: boolean) => {
        setState((prev) =>
            prev.map((s) =>
                s.id !== (supplier?.id ?? '')
                    ? s
                    : {
                          ...s,
                          logins: s.logins.map((l) =>
                              l.email === email
                                  ? { ...l, access: { ...l.access, [siteId]: granted } }
                                  : l,
                          ),
                      },
            ),
        );
        getSupplierControlService().setSiteAccess(email, siteId, granted);
    };

    if (state.length === 0) {
        return (
            <Card>
                <div style={{ padding: 30, textAlign: 'center', color: C.textMuted }}>
                    No suppliers to show yet.
                </div>
            </Card>
        );
    }

    return (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            {/* Supplier list */}
            <Card style={{ width: 260, flexShrink: 0 }}>
                <div style={{ padding: 12, borderBottom: `1px solid ${C.borderLight}` }}>
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search suppliers…"
                        style={{
                            width: '100%',
                            height: 34,
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            padding: '0 10px',
                            fontSize: T.body,
                            fontFamily: F.ui,
                            boxSizing: 'border-box',
                        }}
                    />
                </div>
                {list.map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                            setSelectedId(s.id);
                            setManageEmail(null);
                        }}
                        style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: `1px solid ${C.borderLight}`,
                            background: s.id === supplier?.id ? C.primarySoft : 'transparent',
                            borderLeft:
                                s.id === supplier?.id
                                    ? `3px solid ${C.purple}`
                                    : '3px solid transparent',
                            borderTop: 'none',
                            borderRight: 'none',
                            fontFamily: F.ui,
                        }}
                    >
                        <div style={{ fontSize: T.body, fontWeight: 600, color: C.text }}>{s.name}</div>
                        <div style={{ fontSize: T.caption, color: C.textMuted }}>
                            {s.sites.length} sites · {s.logins.length} login(s)
                        </div>
                    </button>
                ))}
            </Card>

            {/* Supplier detail */}
            <div style={{ flex: 1 }}>
                <Card style={{ marginBottom: 16 }}>
                    <div style={{ padding: 20 }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: T.section, fontWeight: 700, color: C.text }}>
                            {supplier?.name}
                        </h3>
                        <div style={{ display: 'flex', gap: 28 }}>
                            <div>
                                <div style={{ fontSize: T.caption, color: C.textMuted }}>Sites</div>
                                <div style={{ fontSize: T.body, color: C.text }}>
                                    {supplier?.sites.length ?? 0}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: T.caption, color: C.textMuted }}>Logins</div>
                                <div style={{ fontSize: T.body, color: C.text }}>
                                    {supplier?.logins.length ?? 0}
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card>
                    <div
                        style={{
                            padding: '12px 18px',
                            borderBottom: `1px solid ${C.borderLight}`,
                            fontSize: T.body,
                            fontWeight: 600,
                            color: C.text,
                        }}
                    >
                        Logins
                    </div>
                    {supplier && supplier.logins.length > 0 ? (
                        supplier.logins.map((l) => {
                            const granted = supplier.sites.filter((s) => l.access[s.id]).length;
                            return (
                                <div
                                    key={l.email}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '14px 18px',
                                        borderTop: `1px solid ${C.borderLight}`,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontSize: T.body, fontFamily: F.mono, color: C.primary }}>
                                            {l.email}
                                        </div>
                                        <div style={{ fontSize: T.caption, color: C.textMuted, marginTop: 2 }}>
                                            Access to{' '}
                                            <strong style={{ color: granted ? C.green : C.textMuted }}>
                                                {granted}
                                            </strong>{' '}
                                            of {supplier.sites.length} sites
                                        </div>
                                    </div>
                                    <Btn
                                        label="Manage access"
                                        variant="sec"
                                        size="xs"
                                        onClick={() => setManageEmail(l.email)}
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <div style={{ padding: 24, textAlign: 'center', color: C.textMuted }}>
                            No logins to show yet.
                        </div>
                    )}
                </Card>
            </div>

            {managed && supplier ? (
                <ManageDrawer
                    email={managed.email}
                    sites={supplier.sites}
                    access={managed.access}
                    onGrant={(siteId) => setAccess(managed.email, siteId, true)}
                    onRevoke={(site) => setConfirm({ email: managed.email, site })}
                    onClose={() => setManageEmail(null)}
                />
            ) : null}

            {confirm ? (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.35)',
                        zIndex: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <button
                        type="button"
                        aria-label="Cancel"
                        onClick={() => setConfirm(null)}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'default',
                        }}
                    />
                    <div
                        role="dialog"
                        aria-modal="true"
                        style={{
                            position: 'relative',
                            width: 420,
                            background: C.surface,
                            borderRadius: 12,
                            padding: 24,
                            boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
                            fontFamily: F.ui,
                        }}
                    >
                        <h3 style={{ margin: '0 0 8px', fontSize: T.section, fontWeight: 700, color: C.text }}>
                            Revoke access?
                        </h3>
                        <p style={{ margin: '0 0 18px', fontSize: T.body, color: C.textSub, lineHeight: 1.6 }}>
                            Remove <strong>{confirm.site.name}</strong> ({confirm.site.code}) access for{' '}
                            <span style={{ fontFamily: F.mono }}>{confirm.email}</span>? This takes effect
                            immediately.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <Btn label="Cancel" variant="ghost" onClick={() => setConfirm(null)} />
                            <Btn
                                label="Revoke access"
                                variant="danger"
                                onClick={() => {
                                    setAccess(confirm.email, confirm.site.id, false);
                                    setConfirm(null);
                                }}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

// ── Approval queue ────────────────────────────────────────────────────────────
function QueueView({
    requests,
    approvingId,
    onApprove,
}: {
    requests: ControlRequest[];
    approvingId: string | null;
    onApprove: (id: string) => void;
}) {
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = requests.filter(
        (r) =>
            query === '' ||
            r.email.toLowerCase().includes(query) ||
            r.supplier.toLowerCase().includes(query),
    );
    const headers = ['Email', 'Supplier', 'Sites', 'Status', 'Submitted', ''];

    return (
        <>
            <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by email or supplier…"
                style={{
                    width: '100%',
                    maxWidth: 360,
                    height: 38,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    padding: '0 12px',
                    fontSize: T.body,
                    fontFamily: F.ui,
                    marginBottom: 14,
                    boxSizing: 'border-box',
                }}
            />
            <Card>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                    <thead>
                        <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                            {headers.map((h, i) => (
                                <th key={h || `blank-${i}`} style={{ padding: '10px 16px', fontWeight: 600 }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={headers.length}
                                    style={{ padding: 30, textAlign: 'center', color: C.textMuted }}
                                >
                                    No pending requests to show yet.
                                </td>
                            </tr>
                        ) : (
                            shown.map((r) => (
                                <tr key={r.id} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                                    <td style={{ padding: '10px 16px', fontFamily: F.mono, color: C.primary }}>
                                        {r.email}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontWeight: 600, color: C.text }}>
                                        {r.supplier}
                                    </td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <Pill label={`${r.sites} site${r.sites > 1 ? 's' : ''}`} color="blue" />
                                    </td>
                                    <td style={{ padding: '10px 16px' }}>
                                        {r.verified ? (
                                            <Pill label="Email verified · Awaiting approval" color="amber" dot />
                                        ) : (
                                            <Pill label="Awaiting email verification" color="gray" dot />
                                        )}
                                    </td>
                                    <td style={{ padding: '10px 16px', color: C.textMuted }}>{r.submitted}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                                        {r.verified ? (
                                            <Btn
                                                label={approvingId === r.id ? 'Approving…' : 'Approve'}
                                                variant="pri"
                                                size="xs"
                                                disabled={approvingId !== null}
                                                onClick={() => onApprove(r.id)}
                                            />
                                        ) : (
                                            <span style={{ fontSize: T.caption, color: C.textMuted }}>
                                                Not ready
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </Card>
        </>
    );
}

// ── Supplier review (per-site grant decisions for one pending login) ──────────
type Decision = 'pending' | 'approved' | 'rejected';

function ReviewView({
    request,
    sites,
    onBack,
}: {
    request: ControlRequest | null;
    sites: ControlSite[];
    onBack: () => void;
}) {
    const allTo = (d: Decision): Record<string, Decision> =>
        Object.fromEntries(sites.map((s) => [s.id, d]));
    const [decisions, setDecisions] = useState<Record<string, Decision>>(allTo('pending'));
    const [done, setDone] = useState(false);
    useEffect(() => setDecisions(Object.fromEntries(sites.map((s) => [s.id, 'pending']))), [sites]);

    const set = (id: string, d: Decision) => setDecisions((prev) => ({ ...prev, [id]: d }));
    const allDecided = sites.length > 0 && sites.every((s) => decisions[s.id] !== 'pending');
    const approvedCount = sites.filter((s) => decisions[s.id] === 'approved').length;

    if (!request) {
        return (
            <Card>
                <div style={{ padding: 30, textAlign: 'center', color: C.textMuted }}>
                    No request selected.{' '}
                    <button
                        type="button"
                        onClick={onBack}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: C.primary,
                            cursor: 'pointer',
                            fontFamily: F.ui,
                            fontSize: T.body,
                        }}
                    >
                        Back to queue
                    </button>
                </div>
            </Card>
        );
    }

    if (done) {
        return (
            <Card style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ marginBottom: 10 }}>
                    <Pill label="Decision recorded" color="green" dot />
                </div>
                <p style={{ margin: '8px 0 16px', fontSize: T.body, color: C.textSub }}>
                    {request.email} was granted access to {approvedCount} of {sites.length} site(s).
                </p>
                <Btn label="Back to queue" variant="sec" onClick={onBack} />
            </Card>
        );
    }

    return (
        <div style={{ maxWidth: 860 }}>
            <div style={{ marginBottom: 12 }}>
                <button
                    type="button"
                    onClick={onBack}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: C.primary,
                        cursor: 'pointer',
                        fontFamily: F.ui,
                        fontSize: T.body,
                        padding: 0,
                    }}
                >
                    ← Back to queue
                </button>
            </div>

            <Card style={{ marginBottom: 16 }}>
                <div style={{ padding: 20 }}>
                    <h3 style={{ margin: 0, fontSize: T.section, fontWeight: 700, color: C.text }}>
                        {request.supplier}
                    </h3>
                    <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Pill label="Pending login" color="amber" dot />
                        <span style={{ fontSize: T.body, color: C.textMuted, fontFamily: F.mono }}>
                            {request.email}
                        </span>
                    </div>
                </div>
            </Card>

            <Card>
                <div
                    style={{
                        padding: '12px 18px',
                        borderBottom: `1px solid ${C.borderLight}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <span style={{ fontSize: T.body, fontWeight: 600, color: C.text }}>
                        Grant site access
                    </span>
                    <span style={{ fontSize: T.caption, color: C.textMuted }}>
                        {approvedCount} of {sites.length} granted
                    </span>
                </div>
                {sites.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: C.textMuted }}>
                        No sites to show yet.
                    </div>
                ) : (
                    sites.map((s) => {
                        const d = decisions[s.id];
                        return (
                            <div
                                key={s.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '14px 18px',
                                    borderTop: `1px solid ${C.borderLight}`,
                                }}
                            >
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: T.body, fontWeight: 600, color: C.text }}>
                                            {s.name}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: T.caption,
                                                fontFamily: F.mono,
                                                color: C.textSub,
                                                background: C.graySoft,
                                                borderRadius: 4,
                                                padding: '1px 6px',
                                            }}
                                        >
                                            {s.code}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: T.caption, color: C.textMuted, marginTop: 2 }}>
                                        {s.location}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {d !== 'pending' ? (
                                        <Pill
                                            label={d === 'approved' ? 'Access: Yes' : 'Access: No'}
                                            color={d === 'approved' ? 'green' : 'red'}
                                            dot
                                        />
                                    ) : null}
                                    <Btn
                                        label="Yes"
                                        variant={d === 'approved' ? 'pri' : 'sec'}
                                        size="xs"
                                        onClick={() => set(s.id, 'approved')}
                                    />
                                    <Btn
                                        label="No"
                                        variant={d === 'rejected' ? 'danger' : 'sec'}
                                        size="xs"
                                        onClick={() => set(s.id, 'rejected')}
                                    />
                                </div>
                            </div>
                        );
                    })
                )}
                {sites.length > 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            padding: '12px 18px',
                            borderTop: `1px solid ${C.borderLight}`,
                            background: C.bg,
                        }}
                    >
                        <Btn
                            label="Approve all"
                            variant="sec"
                            size="xs"
                            onClick={() => setDecisions(allTo('approved'))}
                        />
                        <Btn
                            label="Reject all"
                            variant="sec"
                            size="xs"
                            onClick={() => setDecisions(allTo('rejected'))}
                        />
                    </div>
                ) : null}
            </Card>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Btn
                    label={allDecided ? 'Confirm decisions' : 'Decide all sites first'}
                    variant="pri"
                    size="md"
                    disabled={!allDecided}
                    onClick={() => allDecided && setDone(true)}
                />
            </div>
        </div>
    );
}

// ── Users (ops view: supplier vs employee) ───────────────────────────────────
const USER_TYPE_FILTERS: { id: UserType | 'all'; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'supplier', label: 'Suppliers' },
    { id: 'employee', label: 'Employees' },
];

function UsersView({ users }: { users: ControlUser[] }) {
    const [q, setQ] = useState('');
    const [typeFilter, setTypeFilter] = useState<UserType | 'all'>('all');

    const query = q.trim().toLowerCase();
    const shown = users.filter(
        (u) =>
            (typeFilter === 'all' || u.type === typeFilter) &&
            (query === '' ||
                u.email.toLowerCase().includes(query) ||
                u.username.toLowerCase().includes(query)),
    );
    const headers = ['User', 'Type', 'Roles', 'Status', 'Created'];

    const supplierCount = users.filter((u) => u.type === 'supplier').length;
    const employeeCount = users.filter((u) => u.type === 'employee').length;

    return (
        <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Pill label={`${supplierCount} suppliers`} color="blue" />
                <Pill label={`${employeeCount} employees`} color="purple" />
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    margin: '14px 0',
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ display: 'flex', gap: 6 }}>
                    {USER_TYPE_FILTERS.map((f) => {
                        const on = typeFilter === f.id;
                        return (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => setTypeFilter(f.id)}
                                style={{
                                    padding: '4px 12px',
                                    borderRadius: 20,
                                    fontSize: T.caption,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    fontFamily: F.ui,
                                    background: on ? C.purple : C.surface,
                                    color: on ? '#fff' : C.textSub,
                                    border: `1px solid ${on ? C.purple : C.border}`,
                                }}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by email or username…"
                    style={{
                        width: 300,
                        maxWidth: '100%',
                        height: 38,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        padding: '0 12px',
                        fontSize: T.body,
                        fontFamily: F.ui,
                        boxSizing: 'border-box',
                    }}
                />
            </div>
            <Card>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                    <thead>
                        <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                            {headers.map((h) => (
                                <th key={h} style={{ padding: '10px 16px', fontWeight: 600 }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={headers.length}
                                    style={{ padding: 30, textAlign: 'center', color: C.textMuted }}
                                >
                                    No users to show yet.
                                </td>
                            </tr>
                        ) : (
                            shown.map((u) => (
                                <tr key={u.username} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                                    <td style={{ padding: '10px 16px' }}>
                                        <div style={{ fontWeight: 600, color: C.text }}>{u.email}</div>
                                        <div style={{ fontSize: T.caption, color: C.textMuted, fontFamily: F.mono }}>
                                            {u.username}
                                        </div>
                                    </td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <Pill
                                            label={u.type === 'employee' ? 'Employee' : 'Supplier'}
                                            color={u.type === 'employee' ? 'purple' : 'blue'}
                                            dot
                                        />
                                    </td>
                                    <td style={{ padding: '10px 16px', color: C.textSub }}>
                                        {u.roles.length ? u.roles.join(', ') : '—'}
                                    </td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <Pill
                                            label={u.enabled ? u.status : 'Disabled'}
                                            color={u.enabled ? 'green' : 'gray'}
                                            dot={u.enabled}
                                        />
                                    </td>
                                    <td style={{ padding: '10px 16px', color: C.textMuted }}>{u.created}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </Card>
        </>
    );
}

// ── Main Control area ─────────────────────────────────────────────────────────
export default function SupplierControlScreen() {
    const [view, setView] = useState<ControlView>('suppliers');
    const [suppliers, setSuppliers] = useState<ControlSupplier[]>([]);
    const [requests, setRequests] = useState<ControlRequest[]>([]);
    const [users, setUsers] = useState<ControlUser[]>([]);
    const [reviewId, setReviewId] = useState<string | null>(null);
    const [approvingId, setApprovingId] = useState<string | null>(null);
    const [approveError, setApproveError] = useState<string | null>(null);

    const approve = (id: string) => {
        setApprovingId(id);
        setApproveError(null);
        getSupplierControlService()
            .approveRequest(id)
            .then((refreshed) => setRequests(refreshed))
            .catch((e) =>
                setApproveError(e instanceof Error ? e.message : 'Failed to approve request.'),
            )
            .finally(() => setApprovingId(null));
    };

    useEffect(() => {
        let live = true;
        const svc = getSupplierControlService();
        Promise.all([svc.listSuppliers(), svc.listRequests(), svc.listUsers()])
            .then(([s, r, u]) => {
                if (live) {
                    setSuppliers(s);
                    setRequests(r);
                    setUsers(u);
                }
            })
            .catch(() => {
                if (live) {
                    setSuppliers([]);
                    setRequests([]);
                    setUsers([]);
                }
            });
        return () => {
            live = false;
        };
    }, []);

    const reviewRequest = useMemo(
        () => requests.find((r) => r.id === reviewId) ?? null,
        [requests, reviewId],
    );
    // Sites for the reviewed request's supplier (matched by name).
    const reviewSites = useMemo(
        () => suppliers.find((s) => s.name === reviewRequest?.supplier)?.sites ?? [],
        [suppliers, reviewRequest],
    );

    const pendingCount = requests.filter((r) => r.verified).length;

    const titleByView: Record<ControlView, { title: string; subtitle: string }> = {
        suppliers: {
            title: 'Suppliers',
            subtitle: "Browse suppliers and manage each login's site access",
        },
        queue: {
            title: 'Approval Queue',
            subtitle: "Pending supplier logins — grant access to the supplier's sites",
        },
        review: { title: 'Supplier Review', subtitle: 'Grant per-site access for a pending login' },
        users: {
            title: 'Users',
            subtitle: 'All accounts by type — suppliers (password) and employees (Midway SSO)',
        },
    };

    return (
        <Shell
            navItems={CONTROL_NAV}
            active={view === 'review' ? 'queue' : view}
            accent={C.purple}
            onNav={(id) => {
                setView(id as ControlView);
                setReviewId(null);
            }}
        >
            <PageTitle
                title={titleByView[view].title}
                subtitle={titleByView[view].subtitle}
                right={
                    view === 'queue' && pendingCount > 0 ? (
                        <Pill label={`${pendingCount} pending`} color="amber" dot />
                    ) : (
                        <Pill label="Internal" color="purple" dot />
                    )
                }
            />

            {view === 'suppliers' ? <SuppliersView suppliers={suppliers} /> : null}
            {view === 'queue' ? (
                <>
                    {approveError ? (
                        <div style={{ marginBottom: 12, fontSize: T.caption, color: C.red }}>
                            {approveError}
                        </div>
                    ) : null}
                    <QueueView requests={requests} approvingId={approvingId} onApprove={approve} />
                </>
            ) : null}
            {view === 'review' ? (
                <ReviewView
                    request={reviewRequest}
                    sites={reviewSites}
                    onBack={() => {
                        setView('queue');
                        setReviewId(null);
                    }}
                />
            ) : null}
            {view === 'users' ? <UsersView users={users} /> : null}
        </Shell>
    );
}
